/**
 * Homestead panel (B on foot) — the land hub:
 *  - PLACE BEACON → enters the ghost placement flow (build-mode style).
 *  - CURRENT CLAIM (when standing inside your own): tier/upgrade + co-op
 *    allowlist (permit online players; remove builders).
 *  - MY HOMESTEADS: every claim you own across ALL systems, with WARP —
 *    sets the nav destination (the normal jump charge + fuel cost applies).
 * BuildPalette idiom: inline styles, capture listener swallows its own keys,
 * onClose re-grabs pointer lock.
 */
import {
  TIERS,
  MAX_TIER,
  ITEMS,
  PRODUCTION_RECIPES,
  RESIDENT_RANK_XP,
  RESIDENT_RATE_PERMILLE,
  SETTLEMENT_PROGRESSION_MAX,
  SETTLEMENT_SPECIALTIES,
  expandCost,
  facilityUpgradeAfter,
  settlementGrowthStatus,
  settlementApplicantOfferCount,
  settlementFacilityRelicenseTargets,
  settlementFacilityCapacity,
  settlementFacilityFamily,
  settlementFacilityVariant,
  settlementQualityBreakdown,
  settlementFoodReport,
  settlementFoundingStatus,
  settlementStorefrontOffers,
  settlementBankReport,
  settlementGuildOffers,
  settlementGuildSlots,
  settlementLiaisonRank,
  guildOrderPayout,
  settlementResidentAssignmentOptions,
  settlementResidentPersonality,
  npcPersonalitySummary,
  settlementStorageUsed,
  SETTLEMENT_FOUNDER,
  SETTLEMENT_SECOND_FOUNDER,
  SETTLEMENT_BUILDING_DEFS,
  tierDef,
  type Beacon,
  type HomesteadRoom,
  type HomesteadRoomSolution,
  type PlacedPiece,
  type SettlementStateV1,
} from "@hyperlight/shared";

const ACCENT = "#2fb8df";

export interface HomesteadRow {
  beaconId: string;
  systemId: string;
  /** Display label for the system (name, or HOME SYSTEM). */
  label: string;
  tier: number;
  /** This beacon is in the CURRENT system. */
  here: boolean;
  /** Currently HUD-tracked (the ⌂ marker points at it). */
  tracked: boolean;
}

export interface ClaimPanelData {
  /** The own claim the player is standing in, if any. */
  current: { beacon: Beacon; pieceCount: number } | null;
  credits: number;
  /** Other players currently in this system (server resolves their uids). */
  online: { id: number; name: string }[];
  /** Every claim the player owns, across all systems. */
  mine: HomesteadRow[];
  /** Local room recognition plus the latest server-authoritative production
   * snapshot for the claim underfoot. */
  settlement: {
    rooms: HomesteadRoom[];
    roomSolution: HomesteadRoomSolution;
    pieces: PlacedPiece[];
    state: SettlementStateV1 | null;
    inventory: Record<string, number>;
    syncing: boolean;
  } | null;
}

function esc(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[c]!);
}

function duration(ms: number): string {
  const mins = Math.max(1, Math.ceil(ms / 60_000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
}

function settlementItemIds(state: SettlementStateV1, inventory: Readonly<Record<string, number>>): string[] {
  const ids = new Set<string>(Object.keys(state.storage));
  for (const recipe of PRODUCTION_RECIPES) for (const stack of [...recipe.inputs, ...recipe.outputs]) ids.add(stack.id);
  return [...ids].filter((id) => (inventory[id] ?? 0) > 0 || (state.storage[id] ?? 0) > 0
    || PRODUCTION_RECIPES.some((recipe) => recipe.inputs.some((stack) => stack.id === id)));
}

export class ClaimPanel {
  visible = false;
  private el: HTMLElement;

  constructor(private handlers: {
    onExpand: (beaconId: string, tier: number) => void;
    onPermitAdd: (beaconId: string, playerId: number) => void;
    onPermitRemove: (beaconId: string, uid: string) => void;
    onPlaceBeacon: () => void;
    onWarp: (systemId: string, label: string) => void;
    /** Toggle the HUD tracker on an IN-SYSTEM claim. */
    onTrack: (beaconId: string) => void;
    onSettlementRefresh: (beaconId: string) => void;
    onSettlementBootstrap: (beaconId: string) => void;
    onSettlementDeposit: (beaconId: string, item: string, n: number) => void;
    onSettlementWithdraw: (beaconId: string, item: string, n: number) => void;
    onSettlementQueue: (beaconId: string, facilityId: string, recipeId: string, workerIds: string[]) => void;
    onSettlementTarget: (beaconId: string, facilityId: string, recipeId: string | null,
      targetQuantity: number, workerIds: string[]) => void;
    onSettlementUpgradeFacility: (beaconId: string, facilityId: string) => void;
    onSettlementStoreBuy: (beaconId: string, offerId: string) => void;
    onSettlementFoundBank: (beaconId: string, anchorPid: number) => void;
    /** Positive deposits into the Bank; negative withdraws. */
    onSettlementBankTransfer: (beaconId: string, n: number) => void;
    onSettlementGuildOrder: (beaconId: string,
      op: "guildPin" | "guildContribute" | "guildComplete" | "guildAbandon", orderId: string) => void;
    onSettlementPromoteResident: (beaconId: string, residentId: string, facilityId: string,
      specialty: string | null) => void;
    onSettlementReassignResident: (beaconId: string, residentId: string,
      homePid: number, workplacePid: number, facilityId: string) => void;
    onSettlementRelicenseFacility: (beaconId: string, facilityId: string, anchorPid: number) => void;
    onSettlementRecruit: (beaconId: string, applicantId: string,
      profession: SettlementStateV1["applicants"][number]["profession"],
      placement: { homePid: number; workplacePid: number; facilityId: string }) => void;
    onSettlementAdvanceStage: (beaconId: string) => void;
    onSettlementDecommission: (beaconId: string) => void;
    onClose: () => void;
  }) {
    this.el = document.createElement("div");
    this.el.id = "claim-panel";
    this.el.style.cssText =
      "display:none;position:fixed;inset:0;z-index:60;align-items:center;justify-content:center;"
      + "background:rgba(4,8,12,0.72);font-family:'Segoe UI',system-ui,sans-serif;";
    document.body.appendChild(this.el);
    window.addEventListener("keydown", (e) => {
      if (!this.visible) return;
      if (e.code === "Escape" || e.code === "KeyB") {
        e.preventDefault();
        e.stopImmediatePropagation(); // a B here must not re-open/replant
        this.close();
      }
    }, { capture: true });
  }

  open(data: ClaimPanelData): void {
    this.visible = true;
    this.el.style.display = "flex";

    const row = (label: string, value: string): string =>
      `<div style="display:flex;justify-content:space-between;gap:18px;font-size:12px;margin:3px 0;">
        <span style="color:#7c8f9c;">${label}</span><span style="color:#e8f2f8;">${value}</span>
      </div>`;
    const heading = (text: string, mt = 16): string =>
      `<div style="color:${ACCENT};font-size:11px;letter-spacing:0.22em;margin:${mt}px 0 7px;">${text}</div>`;

    // ── current claim (standing inside your own) ──
    let currentSection = "";
    if (data.current) {
      const b = data.current.beacon;
      const t = tierDef(b.tier);
      const atMax = b.tier >= MAX_TIER;
      const next = atMax ? null : TIERS[b.tier + 1];
      const cost = atMax ? 0 : expandCost(b.tier, b.tier + 1);
      const canAfford = data.credits >= cost;
      const builders = b.allowlist.length === 0
        ? '<div style="color:#7c8f9c;font-size:11px;">No one else may build here yet.</div>'
        : b.allowlist.map((uid) =>
          `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin:4px 0;">
            <span style="color:#e8f2f8;font-size:12px;">${uid.slice(0, 10)}…</span>
            <button class="cp-rm" data-uid="${uid}" style="all:unset;flex-shrink:0;white-space:nowrap;cursor:pointer;color:#ff5a4a;font-size:11px;
              border:1px solid rgba(255,90,74,0.5);border-radius:3px;padding:2px 8px;">REMOVE</button>
          </div>`).join("");
      const online = data.online.length === 0
        ? '<div style="color:#7c8f9c;font-size:11px;">No other players in this system right now.</div>'
        : data.online.map((p) =>
          `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin:4px 0;">
            <span style="color:#e8f2f8;font-size:12px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${p.name}</span>
            <button class="cp-add" data-player="${p.id}" style="all:unset;flex-shrink:0;white-space:nowrap;cursor:pointer;color:${ACCENT};font-size:11px;
              border:1px solid rgba(47,184,223,0.5);border-radius:3px;padding:2px 8px;">ADD BUILDER</button>
          </div>`).join("");
      const settlement = data.settlement;
      let settlementSection = "";
      if (settlement) {
        const roomRows = settlement.rooms.length === 0
          ? '<div style="color:#d59a62;font-size:11px;">No sealed, door-accessible rooms recognized.</div>'
          : settlement.rooms.map((room) => {
            const functions = room.functions.length > 0 ? room.functions.join(" · ") : "unassigned room";
            return `<div style="display:flex;justify-content:space-between;gap:12px;margin:4px 0;font-size:11px;">
              <span style="color:#e8f2f8;text-transform:uppercase;">${esc(functions)}</span>
              <span style="color:#7c8f9c;white-space:nowrap;">${room.floorAreaM2.toFixed(1)} m² · C${room.comfort}</span>
            </div>`;
          }).join("");
        const state = settlement.state;
        let management = '<div style="color:#7c8f9c;font-size:11px;">SYNCING AUTHORITATIVE PROGRESS…</div>';
        if (state) {
          const founding = settlementFoundingStatus(settlement.roomSolution, settlement.pieces, state);
          if (founding.step !== "complete") {
            const hasFirstFriend = state.residents.some((resident) => resident.id === SETTLEMENT_FOUNDER.id);
            const canInvite = hasFirstFriend ? founding.step === "invite-second-founder" : founding.ready;
            const friend = hasFirstFriend ? SETTLEMENT_SECOND_FOUNDER : SETTLEMENT_FOUNDER;
            const requirement = hasFirstFriend
              ? `SECOND FRIEND: ${founding.step === "furnish-second-dwelling" ? "ADD A SECOND BED + LIGHT"
                : founding.step === "furnish-synthesizer" ? "PLACE THE MATERIA SYNTHESIZER"
                  : founding.step === "connect-synthesizer-home" ? "CONNECT THE SYNTHESIZER TO THE SECOND HOME"
                    : "SYNTHESIZER READY"}`
              : `FIRST FRIEND: ${founding.hasDwelling ? "✓" : "○"} LIVING · ${founding.hasBiogenerator ? "✓" : "○"} BIOGENERATOR · ${founding.hasStorage ? "✓" : "○"} STORAGE`;
            management = `<div style="color:${canInvite ? "#9ed7b1" : "#7c8f9c"};font-size:11px;line-height:1.5;margin-bottom:8px;">
              ${esc(requirement)}<br>${canInvite ? `${esc(friend.name)} can join as a Rank 1 ${esc(friend.profession)}.` : "BUILD MODE HIGHLIGHTS THE NEXT REQUIRED PIECE."}
            </div>
            <button id="cp-st-bootstrap" ${canInvite ? "" : "disabled"} style="all:unset;display:block;width:100%;box-sizing:border-box;text-align:center;
              cursor:${canInvite ? "pointer" : "default"};padding:8px;border-radius:4px;border:1px solid ${canInvite ? ACCENT : "rgba(255,255,255,0.18)"};
              color:${canInvite ? ACCENT : "#5a6a75"};font-size:11px;letter-spacing:0.12em;">INVITE ${friend.name.toUpperCase()}</button>`;
          } else {
            const growth = settlementGrowthStatus(state, settlement.rooms);
            const roomForFacility = (facility: SettlementStateV1["facilities"][number]): HomesteadRoom | undefined =>
              settlement.rooms.find((room) => room.functionAnchors[facility.roomFunction] === facility.anchorPid);
            const residentUsesFacility = (resident: SettlementStateV1["residents"][number],
              facility: SettlementStateV1["facilities"][number]): boolean => {
              const room = roomForFacility(facility);
              return (resident.facilityId === undefined || resident.facilityId === facility.id)
                && resident.workplacePid !== null
                && (room?.functionSlots[facility.roomFunction].includes(resident.workplacePid) ?? false);
            };
            const residents = state.residents.map((resident, residentIndex) => {
              const workplace = state.facilities.find((facility) => residentUsesFacility(resident, facility));
              const targetRank = Math.min(5, resident.rank + 1) as keyof typeof RESIDENT_RANK_XP;
              const rankOpen = resident.rank < SETTLEMENT_PROGRESSION_MAX;
              const xpNeed = rankOpen ? RESIDENT_RANK_XP[targetRank] : RESIDENT_RANK_XP[resident.rank];
              const canPromote = rankOpen && !!workplace && resident.xp >= xpNeed;
              const progress = rankOpen ? `${resident.xp} / ${xpNeed} XP` : `${resident.xp} XP · CURRENT CAP`;
              const residentRate = Math.round(RESIDENT_RATE_PERMILLE[resident.rank] / 10);
              const assignments = settlementResidentAssignmentOptions(
                state, resident.id, settlement.roomSolution, settlement.pieces);
              const assignmentOptions = assignments.map((assignment, assignmentIndex) => {
                const homeRoom = settlement.rooms.findIndex((room) =>
                  room.functionSlots.dwelling.includes(assignment.homePid));
                const workRoom = settlement.rooms.findIndex((room) =>
                  room.functionAnchors[assignment.roomFunction] === assignment.facilityAnchorPid);
                const current = resident.homePid === assignment.homePid
                  && resident.workplacePid === assignment.workplacePid
                  && (resident.facilityId === undefined || resident.facilityId === assignment.facilityId);
                return `<option value="${assignmentIndex}" ${current ? "selected" : ""}>DWELLING ${homeRoom + 1} → ${assignment.roomFunction.toUpperCase()} ${workRoom + 1}${current ? " · CURRENT" : ""}</option>`;
              }).join("");
              const relocation = assignments.length > 0
                ? `<div style="display:flex;gap:5px;margin:4px 0 8px;">
                    <select class="cp-st-reassign-choice" data-rs="${residentIndex}" style="min-width:0;flex:1;padding:3px;background:#111b22;color:#d7e8f0;border:1px solid rgba(47,184,223,0.25);font-size:9px;">${assignmentOptions}</select>
                    <button class="cp-st-reassign" data-rs="${residentIndex}" style="all:unset;cursor:pointer;color:${ACCENT};font-size:9px;">MOVE</button>
                  </div>`
                : `<div style="color:#d59a62;font-size:9px;margin:3px 0 7px;">NO VACANT REACHABLE BED / WORKSTATION PAIR</div>`;
              const hungry = resident.fedForDay !== undefined && state.lastMealNight !== undefined
                && resident.fedForDay < state.lastMealNight;
              const mealTag = hungry
                ? ' · <span style="color:#d59a62;">HUNGRY — NOT WORKING TODAY</span>'
                : (resident.mealHappinessPermille ?? 0) > 0
                  ? ` · <span style="color:#9ed7b1;">MEAL +${Math.round((resident.mealHappinessPermille ?? 0) / 10)}%</span>` : "";
              const specialtyOptions = SETTLEMENT_SPECIALTIES[resident.profession ?? ""];
              const specialtyTag = resident.specialty
                ? ` · <span style="color:#a993dc;">${esc(resident.specialty.toUpperCase())} SPECIALIST</span>` : "";
              const specialtySelect = rankOpen && workplace && specialtyOptions
                ? `<select class="cp-st-specialty" data-rs="${residentIndex}" style="margin-left:6px;padding:1px 2px;background:#111b22;color:#d7e8f0;border:1px solid rgba(169,147,220,0.35);font-size:8px;">
                    <option value="">${resident.specialty ? `KEEP ${esc(resident.specialty.toUpperCase())}` : "NO SPECIALTY"}</option>
                    ${specialtyOptions.map((token) => `<option value="${token}">${token.toUpperCase()}</option>`).join("")}
                  </select>` : "";
              return `<div style="font-size:11px;margin:5px 0;color:#e8f2f8;">${esc(resident.name)}
                <span style="color:#7c8f9c;text-transform:uppercase;"> · ${esc(resident.profession ?? "unassigned")} R${resident.rank}${resident.trait ? ` · ${esc(resident.trait)}` : ""} · ${residentRate}% RATE · ${progress}</span>${specialtyTag}${mealTag}
                ${rankOpen && workplace ? `${specialtySelect}<button class="cp-st-promote" data-rs="${residentIndex}" data-fi="${state.facilities.indexOf(workplace)}" ${canPromote ? "" : "disabled"}
                  style="all:unset;margin-left:7px;cursor:${canPromote ? "pointer" : "default"};color:${canPromote ? ACCENT : "#4b5860"};font-size:9px;">PROMOTE TO R${targetRank}</button>` : ""}
                <div style="color:#aebfc6;font-size:10px;margin-top:3px;">${esc(npcPersonalitySummary(settlementResidentPersonality(state.beaconId, resident)))}</div></div>${relocation}`;
            }).join("");
            const applicants = state.applicants.length === 0
              ? `<div style="color:#7c8f9c;font-size:10px;line-height:1.45;">${state.residents.length >= growth.populationCap
    ? `${esc(growth.name.toUpperCase())} POPULATION CAP REACHED — ADVANCE THE SETTLEMENT TO INVITE MORE.`
    : "NO APPLICANTS — ADD A VACANT BED AND A REACHABLE RANK-1 WORKSTATION, THEN REFRESH."}</div>`
              : state.applicants.map((applicant, applicantIndex) => {
                const placements = applicant.placements ?? [{
                  homePid: applicant.homePid,
                  workplacePid: applicant.workplacePid,
                  facilityId: applicant.facilityId,
                  facilityAnchorPid: applicant.facilityAnchorPid,
                  roomFunction: applicant.roomFunction,
                }];
                const expires = Math.max(0, applicant.expiresAt - state.updatedAt);
                const options = placements.map((placement, placementIndex) => {
                  const homeRoom = settlement.rooms.findIndex((room) =>
                    room.functionSlots.dwelling.includes(placement.homePid));
                  const workRoom = settlement.rooms.findIndex((room) =>
                    room.functionSlots[placement.roomFunction].includes(placement.workplacePid));
                  const existing = state.facilities.some((facility) => facility.id === placement.facilityId);
                  const label = `DWELLING ${homeRoom + 1} → ${existing ? "OPEN" : "NEW"} ${placement.roomFunction.toUpperCase()} ${workRoom + 1}`;
                  return `<option value="${placementIndex}">${esc(label)}</option>`;
                }).join("");
                return `<div style="border-left:2px solid rgba(158,215,177,0.35);padding-left:9px;margin:7px 0;">
                  <div style="color:#e8f2f8;font-size:11px;">${esc(applicant.name)}
                    <span style="color:#7c8f9c;text-transform:uppercase;"> · ${esc(applicant.profession)} · ${esc(applicant.trait)}</span></div>
                  <div style="color:#aebfc6;font-size:10px;margin-top:3px;">${esc(npcPersonalitySummary(settlementResidentPersonality(state.beaconId, applicant)))}</div>
                  <div style="color:#7c8f9c;font-size:9px;margin:2px 0 4px;">${placements.length} VALID ASSIGNMENT${placements.length === 1 ? "" : "S"} · OFFER ${duration(expires)}</div>
                  <select class="cp-st-placement" data-ai="${applicantIndex}" style="width:100%;box-sizing:border-box;margin:2px 0 5px;padding:4px;background:#111b22;color:#d7e8f0;border:1px solid rgba(158,215,177,0.3);font-size:9px;">${options}</select>
                  <button class="cp-st-recruit" data-ai="${applicantIndex}" style="all:unset;cursor:pointer;color:#9ed7b1;font-size:9px;letter-spacing:0.08em;">INVITE TO SETTLEMENT</button>
                </div>`;
            }).join("");
            const facilityRows = state.facilities.map((facility, fi) => {
              const workers = state.residents.filter((resident) => residentUsesFacility(resident, facility));
              const variant = settlementFacilityVariant(facility);
              const family = settlementFacilityFamily(facility);
              const capacity = settlementFacilityCapacity(facility);
              const definition = SETTLEMENT_BUILDING_DEFS[variant];
              const jobs = facility.queue.length === 0
                ? `<div style="color:#7c8f9c;font-size:10px;margin:3px 0 6px;">${facility.target ? "TARGET WAITING FOR INPUT OR CAPACITY" : "IDLE — SET A TARGET OR START A PROJECT"}</div>`
                : facility.queue.map((job) => {
                  const recipe = PRODUCTION_RECIPES.find((candidate) => candidate.id === job.recipeId);
                  const rate = job.workerIds.reduce((sum, id, index) => {
                    const worker = state.residents.find((resident) => resident.id === id);
                    return sum + (worker ? (index === 0 || worker.profession === recipe?.profession
                      ? RESIDENT_RATE_PERMILLE[worker.rank] : 500) : 0);
                  }, 0);
                  const remaining = Math.max(0, job.workRequiredMs - job.workDoneMs);
                  const eta = Math.ceil(remaining * 1000 / Math.max(1, rate));
                  const pct = Math.min(100, Math.floor(job.workDoneMs * 100 / job.workRequiredMs));
                  return `<div style="font-size:10px;color:#d7e8f0;margin:4px 0;">
                    ${esc(recipe?.name ?? job.recipeId)} · ${pct}% · ${duration(eta)}${job.pauseReason ? ` · <span style="color:#d59a62;">${esc(job.pauseReason.replaceAll("_", " "))}</span>` : ""}
                  </div>`;
                }).join("");
              const recipes = PRODUCTION_RECIPES.filter((recipe) => recipe.family === family
                && recipe.variants.includes(variant) && recipe.facilityFunctions.includes(facility.roomFunction));
              const upgrade = facilityUpgradeAfter(facility.grade);
              const facilityRoom = roomForFacility(facility);
              const relicenseTargets = settlementFacilityRelicenseTargets(
                state, facility.id, settlement.roomSolution, settlement.pieces);
              const relicenseOptions = relicenseTargets.map((target, targetIndex) => {
                const roomIndex = settlement.rooms.findIndex((room) =>
                  room.functionAnchors[target.roomFunction] === target.anchorPid);
                return `<option value="${targetIndex}">${target.roomFunction.toUpperCase()} ${roomIndex + 1} · ${target.workstationPids.length} STATION${target.workstationPids.length === 1 ? "" : "S"}</option>`;
              }).join("");
              const relicenseRow = relicenseTargets.length > 0
                ? `<div style="display:flex;gap:5px;margin:5px 0;">
                    <select class="cp-st-relicense-choice" data-fi="${fi}" style="min-width:0;flex:1;padding:3px;background:#111b22;color:#d7e8f0;border:1px solid rgba(213,154,98,0.3);font-size:9px;">${relicenseOptions}</select>
                    <button class="cp-st-relicense" data-fi="${fi}" style="all:unset;cursor:pointer;color:#d59a62;font-size:9px;">MOVE FACILITY</button>
                  </div>` : "";
              const upgradeInputsOk = upgrade?.costs.every((stack) => (state.storage[stack.id] ?? 0) >= stack.n) ?? false;
              const upgradeRoomOk = !!facilityRoom;
              const upgradeEnabled = !!upgrade && upgradeInputsOk && upgradeRoomOk;
              const upgradeRow = upgrade ? (() => {
                const costs = upgrade.costs.map((stack) => `${stack.n} ${ITEMS[stack.id]?.name ?? stack.id}`).join(" + ");
                const nextCapacity = settlementFacilityCapacity({ ...facility, grade: upgrade.toGrade });
                return `<button class="cp-st-upgrade" data-fi="${fi}" ${upgradeEnabled ? "" : "disabled"}
                  style="all:unset;display:block;width:100%;box-sizing:border-box;cursor:${upgradeEnabled ? "pointer" : "default"};padding:6px 7px;margin:5px 0;
                  border:1px solid ${upgradeEnabled ? "rgba(158,215,177,0.6)" : "rgba(255,255,255,0.13)"};border-radius:3px;color:${upgradeEnabled ? "#9ed7b1" : "#5a6a75"};font-size:10px;line-height:1.45;">
                  UPGRADE TO L${upgrade.toGrade} ${esc(upgrade.name.toUpperCase())}<br><span style="color:#7c8f9c;">${nextCapacity.inputSlots}/${nextCapacity.outputSlots} I/O · ${nextCapacity.workerSlots} WORKERS · ${nextCapacity.parallelJobs} LANE${nextCapacity.parallelJobs === 1 ? "" : "S"}<br>${esc(costs)} · VISIBLE PACKAGE INCLUDED</span>
                </button>`;
              })() : '<div style="color:#7c8f9c;font-size:9px;margin:4px 0;">MAXIMUM BUILDING LEVEL</div>';
              const recipeButtons = recipes.map((recipe, ri) => {
                const worker = workers.find((resident) => resident.profession === recipe.profession
                  && (recipe.specialty === undefined || resident.specialty === recipe.specialty));
                const rankOk = (worker?.rank ?? 0) >= recipe.minRank;
                const inputsOk = recipe.inputs.every((stack) => (state.storage[stack.id] ?? 0) >= stack.n);
                const queueOk = facility.queue.length < capacity.parallelJobs;
                const enabled = !!worker && rankOk && inputsOk && queueOk;
                const inputs = recipe.inputs.map((stack) => `${stack.n} ${ITEMS[stack.id]?.name ?? stack.id}`).join(" + ");
                const outputs = recipe.outputs.map((stack) => `${stack.n} ${ITEMS[stack.id]?.name ?? stack.id}`).join(" + ");
                return `<button class="cp-st-queue" data-fi="${fi}" data-ri="${ri}" ${enabled ? "" : "disabled"}
                  style="all:unset;display:block;width:100%;box-sizing:border-box;cursor:${enabled ? "pointer" : "default"};padding:6px 7px;margin:4px 0;
                  border:1px solid ${enabled ? "rgba(47,184,223,0.55)" : "rgba(255,255,255,0.13)"};border-radius:3px;color:${enabled ? ACCENT : "#5a6a75"};font-size:10px;line-height:1.45;">
                  ${recipe.targetable ? "RUN ONCE" : "START PROJECT"} · ${esc(recipe.name.toUpperCase())}<br><span style="color:#7c8f9c;">${esc(inputs || "NO CONSUMABLE INPUT")} → ${esc(outputs)} · ${duration(recipe.workMs)} BASE · LEAD R${recipe.minRank}</span>
                </button>${recipe.targetable && worker ? `<button class="cp-st-target" data-fi="${fi}" data-ri="${ri}" style="all:unset;cursor:pointer;color:#9ed7b1;font-size:9px;margin:0 0 5px 7px;">${facility.target?.recipeId === recipe.id ? "STOP TARGET" : `MAINTAIN ${recipe.outputs[0]?.n === 1 ? 20 : Math.max(20, recipe.outputs[0]?.n ?? 20)}`}</button>` : ""}`;
              }).join("");
              const civic = variant === "bank" ? (() => {
                const report = settlementBankReport(state);
                return `<div style="color:#9ed7b1;font-size:10px;margin:5px 0;">BANKED ${report.credits} CR · TOMORROW +${report.nextInterest} CR${report.aboveCap > 0 ? ` · <span style="color:#d59a62;">${report.aboveCap} CR ABOVE THE INTEREST CAP</span>` : ""}</div>
                  <div style="display:flex;gap:12px;margin:2px 0 4px;">
                    <button class="cp-bank-deposit" data-fi="${fi}" ${data.credits >= 1000 ? "" : "disabled"} style="all:unset;cursor:${data.credits >= 1000 ? "pointer" : "default"};color:${data.credits >= 1000 ? "#9ed7b1" : "#4b5860"};font-size:9px;">DEPOSIT 1,000 CR</button>
                    <button class="cp-bank-withdraw" data-fi="${fi}" ${report.credits > 0 ? "" : "disabled"} style="all:unset;cursor:${report.credits > 0 ? "pointer" : "default"};color:${report.credits > 0 ? "#9ed7b1" : "#4b5860"};font-size:9px;">WITHDRAW ALL</button>
                  </div>`;
              })() : variant === "guild-hall" ? (() => {
                const guildOffers = settlementGuildOffers(state, Date.now());
                const orders = state.guildOrders ?? [];
                const slots = settlementGuildSlots(state);
                const orderRows = orders.map((order) => {
                  const filled = order.contributed >= order.quantity;
                  const payout = guildOrderPayout(order, settlementLiaisonRank(state));
                  return `<div style="font-size:10px;color:#d7e8f0;margin:3px 0;">
                    ${esc(ITEMS[order.itemId]?.name ?? order.itemId)} · Q${["low", "medium", "high", "premium", "pristine"].indexOf(order.minQuality) + 1}+ · ${order.contributed}/${order.quantity} · ${payout} CR
                    <button class="cp-guild" data-gop="guildContribute" data-oid="${esc(order.id)}" ${filled ? "disabled" : ""} style="all:unset;margin-left:6px;cursor:${filled ? "default" : "pointer"};color:${filled ? "#4b5860" : ACCENT};font-size:9px;">CONTRIBUTE</button>
                    <button class="cp-guild" data-gop="guildComplete" data-oid="${esc(order.id)}" ${filled ? "" : "disabled"} style="all:unset;margin-left:6px;cursor:${filled ? "pointer" : "default"};color:${filled ? "#9ed7b1" : "#4b5860"};font-size:9px;">COMPLETE ORDER</button>
                    <button class="cp-guild" data-gop="guildAbandon" data-oid="${esc(order.id)}" style="all:unset;margin-left:6px;cursor:pointer;color:#d59a62;font-size:9px;">ABANDON</button>
                  </div>`;
                }).join("");
                const offerRows = guildOffers.map((offer) => `<div style="font-size:10px;color:#7c8f9c;margin:3px 0;">
                    ${esc(ITEMS[offer.itemId]?.name ?? offer.itemId)} · Q${["low", "medium", "high", "premium", "pristine"].indexOf(offer.minQuality) + 1}+ ×${offer.quantity} · ${offer.payout} CR
                    <button class="cp-guild" data-gop="guildPin" data-oid="${esc(offer.id)}" ${orders.length < slots.pinned ? "" : "disabled"} style="all:unset;margin-left:6px;cursor:${orders.length < slots.pinned ? "pointer" : "default"};color:${orders.length < slots.pinned ? "#a993dc" : "#4b5860"};font-size:9px;">PIN</button>
                  </div>`).join("");
                return `<div style="color:#a993dc;font-size:9px;letter-spacing:0.18em;margin:6px 0 2px;">PINNED ORDERS ${orders.length}/${slots.pinned}</div>
                  ${orderRows || '<div style="color:#7c8f9c;font-size:9px;">NONE PINNED</div>'}
                  <div style="color:#a993dc;font-size:9px;letter-spacing:0.18em;margin:6px 0 2px;">GUILD OFFERS · ROTATE DAILY</div>${offerRows}`;
              })() : "";
              const offers = settlementStorefrontOffers(state, facility, Date.now());
              const storefront = offers.length === 0 ? "" : `
                <div style="color:#a993dc;font-size:9px;letter-spacing:0.18em;margin:7px 0 2px;">STOREFRONT · ROTATES DAILY</div>
                ${offers.map((offer) => {
                  const affordable = data.credits >= offer.price;
                  const enabled = !offer.sold && affordable;
                  return `<div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin:3px 0;">
                    <span style="color:${offer.sold ? "#5a6a75" : "#d7e8f0"};font-size:10px;">${esc(ITEMS[offer.itemId]?.name ?? offer.itemId)}${offer.n > 1 ? ` ×${offer.n}` : ""} · Q${["low", "medium", "high", "premium", "pristine"].indexOf(offer.quality) + 1}</span>
                    <button class="cp-st-buy" data-oid="${esc(offer.id)}" ${enabled ? "" : "disabled"}
                      style="all:unset;cursor:${enabled ? "pointer" : "default"};color:${offer.sold ? "#5a6a75" : affordable ? "#a993dc" : "#4b5860"};font-size:9px;white-space:nowrap;">${offer.sold ? "SOLD" : `BUY · ${offer.price} CR`}</button>
                  </div>`;
                }).join("")}`;
              return `<div style="border-left:2px solid rgba(47,184,223,0.35);padding-left:9px;margin:8px 0;">
                <div style="font-size:11px;color:#e8f2f8;text-transform:uppercase;">${esc(definition.name)} · ${family} · L${facility.grade}</div>
                <div style="color:#7c8f9c;font-size:9px;">${capacity.inputSlots}/${capacity.outputSlots} I/O · ${workers.length}/${capacity.workerSlots} WORKERS · ${capacity.parallelJobs} LANE${capacity.parallelJobs === 1 ? "" : "S"}${facility.target ? ` · TARGET ${facility.target.targetQuantity}` : ""}</div>
                ${jobs}${relicenseRow}${upgradeRow}${recipeButtons}${storefront}${civic}
              </div>`;
            }).join("");

            const items = settlementItemIds(state, settlement.inventory);
            const storageRow = (id: string, i: number): string => {
              const carried = settlement.inventory[id] ?? 0;
              const stored = state.storage[id] ?? 0;
              const quality = settlementQualityBreakdown(state, id);
              const qualityText = quality.map((n, index) => n > 0
                ? `Q${index + 1} ${n}` : "").filter(Boolean).join(" · ");
              return `<div style="display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:6px;align-items:center;margin:4px 0;">
                <span style="color:#d7e8f0;font-size:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(ITEMS[id]?.name ?? id)} · YOU ${carried} / STORED ${stored}${qualityText ? `<br><span style="color:#7c8f9c;font-size:9px;">${qualityText}</span>` : ""}</span>
                <button class="cp-st-deposit" data-i="${i}" ${carried > 0 ? "" : "disabled"} style="all:unset;cursor:${carried > 0 ? "pointer" : "default"};color:${carried > 0 ? ACCENT : "#4b5860"};font-size:9px;">DEPOSIT ALL</button>
                <button class="cp-st-withdraw" data-i="${i}" ${stored > 0 ? "" : "disabled"} style="all:unset;cursor:${stored > 0 ? "pointer" : "default"};color:${stored > 0 ? ACCENT : "#4b5860"};font-size:9px;">TAKE ALL</button>
              </div>`;
            };
            // Grouped presentation (§1.2): one good per row, grades inside it;
            // data-i stays an index into `items` so the handlers are untouched.
            const STORAGE_GROUPS: readonly { label: string; match: (id: string) => boolean }[] = [
              { label: "FOOD", match: (id) => ["wheat", "bread", "hearty-meal"].includes(id) },
              { label: "MATERIA", match: (id) => id.startsWith("materia-") },
              { label: "METALS", match: (id) => ["ore-ferrite", "ore-adamant", "ore-runite"].includes(id) },
              { label: "INGOTS", match: (id) => id.startsWith("ingot-") },
              { label: "GOODS", match: () => true },
            ];
            const storageRows = STORAGE_GROUPS.map(({ label, match }, groupIndex) => {
              const rows = items.map((id, i) => ({ id, i })).filter(({ id }) =>
                match(id) && !STORAGE_GROUPS.slice(0, groupIndex).some((group) => group.match(id)));
              if (rows.length === 0) return "";
              return `<div style="color:#8496a3;font-size:9px;letter-spacing:0.18em;margin:7px 0 2px;">${label}</div>`
                + rows.map(({ id, i }) => storageRow(id, i)).join("");
            }).join("");
            const used = settlementStorageUsed(state.storage);
            const canDecommission = used === 0 && state.facilities.every((facility) => facility.queue.length === 0);
            const growthRows = growth.requirements.map((entry) =>
              `<div style="display:flex;justify-content:space-between;gap:10px;margin:3px 0;color:${entry.met ? "#9ed7b1" : "#7c8f9c"};font-size:10px;">
                <span>${entry.met ? "✓" : "○"} ${esc(entry.label.toUpperCase())}</span><span>${Math.min(entry.current, entry.required)} / ${entry.required}</span>
              </div>`).join("");
            const growthPanel = growth.nextAvailable
              ? `${heading(`${growth.name.toUpperCase()} → ${growth.nextName?.toUpperCase()}`, 10)}
                ${growthRows}
                <button id="cp-st-advance" ${growth.ready ? "" : "disabled"} style="all:unset;display:block;width:100%;box-sizing:border-box;text-align:center;
                  cursor:${growth.ready ? "pointer" : "default"};padding:8px;margin-top:7px;border-radius:4px;border:1px solid ${growth.ready ? "#9ed7b1" : "rgba(255,255,255,0.18)"};
                  color:${growth.ready ? "#9ed7b1" : "#5a6a75"};font-size:10px;letter-spacing:0.1em;">ADVANCE TO ${growth.nextName?.toUpperCase()}</button>`
              : `<div style="color:#7c8f9c;font-size:10px;margin:8px 0;">NEXT SETTLEMENT STAGE IS NOT AVAILABLE YET.</div>`;
            const stageCapability = growth.stage >= 1
              ? `<div style="color:#9ed7b1;font-size:9px;line-height:1.45;margin:7px 0;letter-spacing:0.05em;">
                  HOMESTEAD ACTIVE · REACHABLE COMMONS HOST EVENING SOCIAL LIFE · KITCHENS ATTRACT PROVISIONERS
                </div>`
              : `<div style="color:#7c8f9c;font-size:9px;line-height:1.45;margin:7px 0;">
                  HOMESTEAD UNLOCKS A FIVE-RESIDENT CAP, COMMONS SOCIAL LIFE, AND KITCHEN PROVISIONERS.
                </div>`;
            const offerBreadth = settlementApplicantOfferCount(growth.appeal);
            const food = settlementFoodReport(state, Date.now());
            const foodColor = food.warning === "none" ? "#9ed7b1"
              : food.warning === "three-nights" ? "#d59a62" : "#e07a6a";
            const foodLine = food.residents === 0 ? "" : `<div style="display:flex;justify-content:space-between;color:${foodColor};font-size:10px;margin-bottom:5px;">
                <span>FOOD ${food.rations} RATIONS · ${food.nights} NIGHTS</span>
                <span>NEXT NIGHT: ${food.nextNightFed === food.residents ? `ALL ${food.residents} FED` : `ONLY ${food.nextNightFed} OF ${food.residents} FED`}</span>
              </div>`;
            management = `${heading(`SETTLEMENT · ${growth.name.toUpperCase()}`, 10)}
              <div style="display:flex;justify-content:space-between;color:#d7e8f0;font-size:10px;margin-bottom:5px;">
                <span>POPULATION ${state.residents.length} / ${growth.populationCap}</span><span>APPEAL ${growth.appeal}</span>
              </div>${foodLine}<div style="color:#7c8f9c;font-size:9px;margin-bottom:5px;">APPLICANT CHOICE UP TO ${offerBreadth} · APPEAL 8 → 2 · APPEAL 16 → 3</div>
              ${growthPanel}${stageCapability}
              ${heading("RESIDENTS", 10)}${residents}
              ${heading("APPLICANTS", 10)}${applicants}
              ${heading("BUILDINGS", 10)}${(() => {
                const hasBank = state.facilities.some((facility) => settlementFacilityVariant(facility) === "bank");
                const vacant = settlement.rooms.find((room) => room.functionAnchors.exchange !== undefined
                  && !state.facilities.some((facility) => facility.roomFunction === "exchange"
                    && facility.anchorPid === room.functionAnchors.exchange));
                return !hasBank && vacant
                  ? `<button id="cp-st-foundbank" data-anchor="${vacant.functionAnchors.exchange}" style="all:unset;display:block;width:100%;box-sizing:border-box;text-align:center;cursor:pointer;padding:7px;margin:4px 0 7px;border:1px solid rgba(158,215,177,0.5);border-radius:4px;color:#9ed7b1;font-size:10px;letter-spacing:0.1em;">ESTABLISH THE BANK AT THE FREE EXCHANGE</button>` : "";
              })()}${facilityRows}
              ${heading(`STORAGE · ${used} / ${state.storageCapacity}`, 10)}${storageRows || '<div style="color:#7c8f9c;font-size:10px;">EMPTY</div>'}
              <button id="cp-st-decommission" ${canDecommission ? "" : "disabled"} style="all:unset;margin-top:12px;cursor:${canDecommission ? "pointer" : "default"};color:${canDecommission ? "#d59a62" : "#4b5860"};font-size:9px;letter-spacing:0.08em;">DECOMMISSION EMPTY SETTLEMENT</button>`;
          }
        }
        settlementSection = `${heading("SETTLEMENT ROOMS", 14)}${roomRows}
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:12px;">
            <span style="color:${ACCENT};font-size:11px;letter-spacing:0.22em;">PRODUCTION</span>
            <button id="cp-st-refresh" ${settlement.syncing ? "disabled" : ""} style="all:unset;cursor:${settlement.syncing ? "default" : "pointer"};color:#7c8f9c;font-size:9px;">${settlement.syncing ? "SYNCING…" : "REFRESH OFFLINE PROGRESS"}</button>
          </div>${management}`;
      }
      currentSection = `
        ${heading("CURRENT CLAIM")}
        ${row("TIER", `${b.tier + 1} / ${MAX_TIER + 1}`)}
        ${row("RADIUS", `${t.radius} m`)}
        ${row("PIECES", `${data.current.pieceCount} / ${t.pieceBudget}`)}
        <div style="margin:10px 0 4px;">
          ${atMax
    ? '<div style="color:#7c8f9c;font-size:11px;">Maximum tier reached.</div>'
    : `<button id="cp-expand" ${canAfford ? "" : "disabled"} style="all:unset;display:block;width:100%;
          text-align:center;box-sizing:border-box;white-space:normal;line-height:1.5;cursor:${canAfford ? "pointer" : "default"};padding:9px 8px;border-radius:4px;
          border:1px solid ${canAfford ? ACCENT : "rgba(255,255,255,0.2)"};
          color:${canAfford ? ACCENT : "#5a6a75"};font-size:12px;letter-spacing:0.12em;">
          UPGRADE → ${next!.radius} m · ${next!.pieceBudget} PIECES — ${cost} CR${canAfford ? "" : " (SHORT)"}
        </button>`}
        </div>
        ${settlementSection}
        ${heading("BUILDERS", 12)}
        ${builders}
        ${heading("ONLINE IN THIS SYSTEM", 12)}
        ${online}
        <div style="color:#5a6a75;font-size:10px;margin-top:10px;">HOLD B (OUTSIDE THIS PANEL) TO ABANDON — EMPTY BUILDS ONLY</div>`;
    }

    // ── my homesteads roster ──
    const roster = data.mine.length === 0
      ? '<div style="color:#7c8f9c;font-size:11px;">No claims yet — place your first beacon.</div>'
      : data.mine.map((r, i) =>
        `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin:4px 0;">
          <span style="color:#e8f2f8;font-size:12px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${r.label} <span style="color:#7c8f9c;">· TIER ${r.tier + 1}</span></span>
          ${r.here
    ? `<button class="cp-track" data-i="${i}" style="all:unset;flex-shrink:0;white-space:nowrap;cursor:pointer;font-size:11px;
              color:${r.tracked ? "#0a1016" : ACCENT};background:${r.tracked ? ACCENT : "transparent"};
              border:1px solid rgba(47,184,223,0.5);border-radius:3px;padding:2px 8px;">${r.tracked ? "TRACKING ◆" : "TRACK"}</button>`
    : `<button class="cp-warp" data-i="${i}" style="all:unset;flex-shrink:0;white-space:nowrap;cursor:pointer;color:${ACCENT};font-size:11px;
              border:1px solid rgba(47,184,223,0.5);border-radius:3px;padding:2px 8px;">WARP</button>`}
        </div>`).join("");

    this.el.innerHTML = `
      <div style="width:440px;max-width:92vw;box-sizing:border-box;max-height:82vh;overflow-y:auto;overflow-x:hidden;padding:22px 26px;border-radius:6px;
          background:rgba(10,16,22,0.96);border:1px solid rgba(47,184,223,0.35);box-shadow:0 0 40px rgba(0,0,0,0.6);">
        <div style="color:#e8f2f8;font-size:15px;letter-spacing:0.28em;margin-bottom:4px;">HOMESTEADS</div>
        <div style="color:#7c8f9c;font-size:11px;margin-bottom:14px;">B / ESC TO CLOSE</div>
        <button id="cp-place" style="all:unset;display:block;width:100%;text-align:center;cursor:pointer;
            padding:10px 0;border-radius:4px;border:1px solid ${ACCENT};color:${ACCENT};
            font-size:12px;letter-spacing:0.14em;">⌖ PLACE BEACON</button>
        ${currentSection}
        ${heading("MY HOMESTEADS")}
        ${roster}
      </div>`;

    this.el.querySelector("#cp-place")?.addEventListener("click", () => {
      this.close();
      this.handlers.onPlaceBeacon();
    });
    if (data.current) {
      const b = data.current.beacon;
      this.el.querySelector("#cp-expand")?.addEventListener("click", () => this.handlers.onExpand(b.id, b.tier + 1));
      for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-add")) {
        btn.addEventListener("click", () => this.handlers.onPermitAdd(b.id, Number(btn.dataset.player)));
      }
      for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-rm")) {
        btn.addEventListener("click", () => this.handlers.onPermitRemove(b.id, btn.dataset.uid ?? ""));
      }
      const settlement = data.settlement;
      this.el.querySelector("#cp-st-refresh")?.addEventListener("click", () => this.handlers.onSettlementRefresh(b.id));
      this.el.querySelector("#cp-st-bootstrap")?.addEventListener("click", () => this.handlers.onSettlementBootstrap(b.id));
      this.el.querySelector("#cp-st-decommission")?.addEventListener("click", () => this.handlers.onSettlementDecommission(b.id));
      this.el.querySelector("#cp-st-advance")?.addEventListener("click", () => this.handlers.onSettlementAdvanceStage(b.id));
      if (settlement?.state) {
        const itemIds = settlementItemIds(settlement.state, settlement.inventory);
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-deposit")) {
          btn.addEventListener("click", () => {
            const id = itemIds[Number(btn.dataset.i)];
            if (id) this.handlers.onSettlementDeposit(b.id, id, settlement.inventory[id] ?? 0);
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-withdraw")) {
          btn.addEventListener("click", () => {
            const id = itemIds[Number(btn.dataset.i)];
            if (id) this.handlers.onSettlementWithdraw(b.id, id, settlement.state!.storage[id] ?? 0);
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-queue")) {
          btn.addEventListener("click", () => {
            const facility = settlement.state!.facilities[Number(btn.dataset.fi)];
            if (!facility) return;
            const variant = settlementFacilityVariant(facility);
            const family = settlementFacilityFamily(facility);
            const recipes = PRODUCTION_RECIPES.filter((recipe) => recipe.family === family
              && recipe.variants.includes(variant) && recipe.facilityFunctions.includes(facility.roomFunction));
            const recipe = recipes[Number(btn.dataset.ri)];
            const room = settlement.rooms.find((candidate) =>
              candidate.functionAnchors[facility.roomFunction] === facility.anchorPid);
            const worker = settlement.state!.residents.find((resident) => resident.profession === recipe?.profession
              && (recipe?.specialty === undefined || resident.specialty === recipe.specialty)
              && (resident.facilityId === undefined || resident.facilityId === facility.id)
              && resident.workplacePid !== null
              && (room?.functionSlots[facility.roomFunction].includes(resident.workplacePid) ?? false));
            if (recipe && worker) this.handlers.onSettlementQueue(b.id, facility.id, recipe.id, [worker.id]);
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-target")) {
          btn.addEventListener("click", () => {
            const facility = settlement.state!.facilities[Number(btn.dataset.fi)];
            if (!facility) return;
            const variant = settlementFacilityVariant(facility);
            const family = settlementFacilityFamily(facility);
            const recipes = PRODUCTION_RECIPES.filter((recipe) => recipe.family === family
              && recipe.variants.includes(variant) && recipe.facilityFunctions.includes(facility.roomFunction));
            const recipe = recipes[Number(btn.dataset.ri)];
            const room = settlement.rooms.find((candidate) =>
              candidate.functionAnchors[facility.roomFunction] === facility.anchorPid);
            const worker = settlement.state!.residents.find((resident) => resident.profession === recipe?.profession
              && (recipe?.specialty === undefined || resident.specialty === recipe.specialty)
              && (resident.facilityId === undefined || resident.facilityId === facility.id)
              && resident.workplacePid !== null
              && (room?.functionSlots[facility.roomFunction].includes(resident.workplacePid) ?? false));
            if (!recipe || !worker) return;
            const stopping = facility.target?.recipeId === recipe.id;
            const target = recipe.outputs[0]?.n === 1 ? 20 : Math.max(20, recipe.outputs[0]?.n ?? 20);
            this.handlers.onSettlementTarget(b.id, facility.id, stopping ? null : recipe.id,
              stopping ? 0 : target, [worker.id]);
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-upgrade")) {
          btn.addEventListener("click", () => {
            const facility = settlement.state!.facilities[Number(btn.dataset.fi)];
            if (facility) this.handlers.onSettlementUpgradeFacility(b.id, facility.id);
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-buy")) {
          btn.addEventListener("click", () => {
            if (btn.dataset.oid) this.handlers.onSettlementStoreBuy(b.id, btn.dataset.oid);
          });
        }
        this.el.querySelector<HTMLElement>("#cp-st-foundbank")?.addEventListener("click", (event) => {
          const anchor = Number((event.currentTarget as HTMLElement).dataset.anchor);
          if (Number.isInteger(anchor)) this.handlers.onSettlementFoundBank(b.id, anchor);
        });
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-bank-deposit")) {
          btn.addEventListener("click", () => this.handlers.onSettlementBankTransfer(b.id, 1000));
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-bank-withdraw")) {
          btn.addEventListener("click", () => this.handlers.onSettlementBankTransfer(
            b.id, -(settlement.state?.bank?.credits ?? 0)));
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-guild")) {
          btn.addEventListener("click", () => {
            const gop = btn.dataset.gop as "guildPin" | "guildContribute" | "guildComplete" | "guildAbandon";
            if (btn.dataset.oid && gop) this.handlers.onSettlementGuildOrder(b.id, gop, btn.dataset.oid);
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-promote")) {
          btn.addEventListener("click", () => {
            const residentIndex = Number(btn.dataset.rs);
            const resident = settlement.state!.residents[residentIndex];
            const facility = settlement.state!.facilities[Number(btn.dataset.fi)];
            const choice = this.el.querySelector<HTMLSelectElement>(
              `.cp-st-specialty[data-rs="${residentIndex}"]`)?.value ?? "";
            if (resident && facility) {
              this.handlers.onSettlementPromoteResident(b.id, resident.id, facility.id, choice || null);
            }
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-reassign")) {
          btn.addEventListener("click", () => {
            const residentIndex = Number(btn.dataset.rs);
            const resident = settlement.state!.residents[residentIndex];
            const select = this.el.querySelector<HTMLSelectElement>(`.cp-st-reassign-choice[data-rs="${residentIndex}"]`);
            if (!resident || !select) return;
            const assignment = settlementResidentAssignmentOptions(
              settlement.state!, resident.id, settlement.roomSolution, settlement.pieces)[Number(select.value)];
            if (assignment) this.handlers.onSettlementReassignResident(
              b.id, resident.id, assignment.homePid, assignment.workplacePid, assignment.facilityId);
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-relicense")) {
          btn.addEventListener("click", () => {
            const facilityIndex = Number(btn.dataset.fi);
            const facility = settlement.state!.facilities[facilityIndex];
            const select = this.el.querySelector<HTMLSelectElement>(`.cp-st-relicense-choice[data-fi="${facilityIndex}"]`);
            if (!facility || !select) return;
            const target = settlementFacilityRelicenseTargets(
              settlement.state!, facility.id, settlement.roomSolution, settlement.pieces)[Number(select.value)];
            if (target) this.handlers.onSettlementRelicenseFacility(b.id, facility.id, target.anchorPid);
          });
        }
        for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-st-recruit")) {
          btn.addEventListener("click", () => {
            const applicantIndex = Number(btn.dataset.ai);
            const applicant = settlement.state!.applicants[applicantIndex];
            const select = this.el.querySelector<HTMLSelectElement>(`.cp-st-placement[data-ai="${applicantIndex}"]`);
            if (!applicant || !select) return;
            const placement = (applicant.placements ?? [{
              homePid: applicant.homePid,
              workplacePid: applicant.workplacePid,
              facilityId: applicant.facilityId,
              facilityAnchorPid: applicant.facilityAnchorPid,
              roomFunction: applicant.roomFunction,
            }])[Number(select.value)];
            if (placement) this.handlers.onSettlementRecruit(b.id, applicant.id, applicant.profession, placement);
          });
        }
      }
    }
    for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-track")) {
      btn.addEventListener("click", () => {
        const r = data.mine[Number(btn.dataset.i)];
        if (r) {
          this.close();
          this.handlers.onTrack(r.beaconId);
        }
      });
    }
    for (const btn of this.el.querySelectorAll<HTMLElement>(".cp-warp")) {
      btn.addEventListener("click", () => {
        const r = data.mine[Number(btn.dataset.i)];
        if (r) {
          this.close();
          this.handlers.onWarp(r.systemId, r.label);
        }
      });
    }
    this.el.addEventListener("click", (ev) => {
      if (ev.target === this.el) this.close(); // backdrop cancels
    });
  }

  close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.el.style.display = "none";
    this.el.innerHTML = "";
    this.handlers.onClose();
  }
}
